import type { HelpGuide } from '../types';

export const glschedGuide: HelpGuide = {
  key: 'glsched',
  title: 'กระทบยอด GL & Open Item Management',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ กระทบยอด GL & รายละเอียดประกอบบัญชี',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/glsched/คู่มือการใช้งาน_กระทบยอดGL-OpenItem_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “กระทบยอด GL & รายละเอียดประกอบบัญชี (Open Item Management)” ใช้กระทบยอดบัญชีแยกประเภทที่ต้องติดตามเป็นรายรายการ (เช่น เงินทดรองจ่าย ค่าใช้จ่ายจ่ายล่วงหน้า เจ้าหนี้การค้า ภาษีหัก ณ ที่จ่าย รายได้รับล่วงหน้า) ให้ ยอดคงเหลือใน GL เท่ากับ ผลรวมของรายการค้าง (Open Items) ที่ยังไม่ปิด แล้วทยอย จับคู่ปิด รายการที่หักล้างกันออกไป จนเหลือเฉพาะรายการที่ยังค้างจริง',
        },
        {
          kind: 'callout',
          text: 'หลักการ Open Item Management 3 ขั้น: ① Reference Key บังคับต่อบัญชี (ทุก JV ที่ลงบัญชีติดตามต้องมี Ref เพื่อใช้จับคู่) · ② Auto-clearing เมื่อ matchKey ตรงกัน (รายการที่อ้างอิงเดียวกันและผลรวม = 0 จะปิดอัตโนมัติ) · ③ Schedule = ผลรวม Open Items (ยอดประกอบบัญชีต้องเท่ากับยอด GL คงเหลือ)',
        },
        { kind: 'para', text: 'หน้าจอมี 4 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ภาพรวมบัญชี (Dashboard) — ',
              text: 'ตารางบัญชีที่ติดตามทั้งหมด พร้อมยอด GL จำนวน Open Items รายการค้างนาน และป้ายสถานะกระทบยอด (แท็บเริ่มต้น)',
            },
            {
              lead: 'รายละเอียดประกอบบัญชี — ',
              text: 'เจาะรายบัญชี: แถบ GL = Schedule = ผลต่าง การกระจายอายุ และตารางรายการค้างทีละรายการ',
            },
            {
              lead: 'Manual Clearing — จับคู่ปิดรายการ — ',
              text: 'ตรวจ Auto-clear ก่อนทำ (dry-run) + worklist รายการค้าง + ยกเลิกการจับคู่กลุ่มที่จับคู่แล้ว',
            },
            {
              lead: 'ตั้งค่าบัญชีที่ติดตาม — ',
              text: 'ตั้งกฎต่อบัญชี: Clearing Rule, Auto-clear, บังคับใส่ Ref, Tolerance, บัญชีตัดจำหน่าย + เพิ่มบัญชีติดตาม',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/glsched/01-overview-dashboard.png',
          caption:
            'แดชบอร์ดภาพรวม — KPI 4 ใบ, แบนเนอร์หลักการ Open Item 3 ขั้น และตารางบัญชีที่ติดตามพร้อมป้ายกระทบยอด',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบน:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'บัญชีที่ติดตาม (Monitored) — ',
              text: 'จำนวนบัญชีที่เปิดติดตาม แยกเป็น Asset / Liability',
            },
            {
              lead: 'กระทบยอดแล้ว (GL = Open Items) — ',
              text: 'จำนวนบัญชีที่ยอด GL ตรงกับผลรวม Open Items (เขียวเมื่อครบ 100%)',
            },
            {
              lead: 'Open Items รวม — ',
              text: 'จำนวนรายการค้างทั้งหมดที่รอจับคู่ปิด (Clearing)',
            },
            {
              lead: 'Open Items ค้างนาน >60 วัน — ',
              text: 'รายการค้างเกิน 60 วันที่ควรเร่งติดตาม',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มแถบบนสุด: เลือก งวด · ป้ายสถานะ Snapshot (DRAFT / รับรองแล้ว ✓ / ล็อกแล้ว 🔒) · Sync รายการใหม่ (ดึง JV ใหม่ ๆ เข้า GL Schedule แบบ idempotent) · Export ประกอบบัญชี (ZIP) (ออกทุกบัญชีเป็น workbook หลายชีต) · ปิดงวด & Lock (ปุ่มสีแดง) ตารางภาพรวมมีคอลัมน์: รหัส · ชื่อบัญชี · ประเภท · โมดูลที่เกี่ยวข้อง · Reference Key · ยอด GL คงเหลือ · Open Items · ค้าง >60 วัน · อายุเฉลี่ย · กระทบยอด · การกระทำ — คลิก ดู Schedule → ที่แถวเพื่อเจาะรายละเอียดบัญชีนั้น',
        },
      ],
    },
    {
      id: 'schedule',
      heading: '3. รายละเอียดประกอบบัญชี (Schedule)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ รายละเอียดประกอบบัญชี (หรือคลิก ดู Schedule → จากภาพรวม) เลือกบัญชีจากกล่อง <select> ด้านบน แล้วระบบจะแสดงแถบกระทบยอดสามช่อง ยอด GL คงเหลือ = ผลรวม Schedule (Open Items) = ผลต่าง (Delta) ถ้าผลต่างอยู่ในวงเงิน Tolerance จะขึ้น “กระทบยอดเรียบร้อย ✓” (เขียว) ถ้าไม่ตรงจะขึ้น “ผลต่างยังไม่ตรง ✗” (แดง) ใต้แถบมีแถบกระจายอายุ Open Items (0-30 / 31-60 / 61-90 / 91-180 / เกิน 180 วัน) คลิกการ์ดอายุเพื่อกรองตาราง หรือกด แสดงทั้งหมด เพื่อล้างตัวกรอง',
        },
        {
          kind: 'para',
          text: 'ตารางรายการค้างแสดง: ลำดับ · Ref · วันที่ · คำอธิบาย/ผู้ที่เกี่ยวข้อง · หน่วยงาน · ยอดคงค้าง · อายุ (วัน) · JV อ้างอิง · โมดูล · ติดตาม · การกระทำ แต่ละแถวมีกล่องเลือกสถานะติดตาม (ไม่ระบุ / กำลังติดตาม / ยกระดับ / แก้ไขแล้ว) และลิงก์ ดู JV เพื่อเปิดใบสำคัญต้นทาง ปุ่มเครื่องมือ: พิมพ์ · Excel · PDF ประกอบบัญชี · Export',
        },
        {
          kind: 'image',
          src: '/help/glsched/02-schedule-reconciliation.png',
          caption:
            'รายละเอียดประกอบบัญชี — แถบ GL = Schedule = ผลต่าง (กระทบยอดเรียบร้อย ✓) และแถบกระจายอายุ Open Items',
        },
      ],
    },
    {
      id: 'match',
      heading: '4. การจับคู่ปิดรายการด้วยมือ (Match)',
      blocks: [
        {
          kind: 'para',
          text: 'ในตารางรายละเอียดประกอบบัญชี ติ๊กเลือกตั้งแต่ 2 รายการขึ้นไปที่ผลรวมหักล้างกันเป็น 0 ระบบจะขึ้นป้าย “เลือก N รายการ · ผลรวม ✓” และเปิดใช้งานปุ่ม จับคู่ปิด (Match) กดเพื่อปิดรายการคู่นั้นพร้อมกัน',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'จับคู่ปิด (Match) — ',
              text: 'ใช้เมื่อเลือก ≥2 รายการและผลรวม = 0 (ปิดรายการออกจาก Open Items)',
            },
            {
              lead: 'จับคู่บางส่วน (Partial) — ',
              text: 'แสดงเมื่อเลือก ≥2 รายการแต่ผลรวม ≠ 0 (ผูกรายการเข้าด้วยกันแต่ยังคงเปิดค้าง)',
            },
            {
              lead: 'ตัดจำหน่ายส่วนต่าง (Write-off) — ',
              text: 'แสดงเมื่อเลือก ≥1 รายการและยังมีส่วนต่างที่อยู่ในวงเงิน Tolerance (ระบบลง JV ตัดจำหน่ายอัตโนมัติ ต้องระบุเหตุผล/Reason Code)',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/glsched/03-schedule-match-selected.png',
          caption:
            'เลือก 2 รายการที่ผลรวม = 0 → ป้าย “เลือก 2 รายการ · ผลรวม ✓” และปุ่ม จับคู่ปิด (Match) เปิดใช้งาน',
        },
      ],
    },
    {
      id: 'clearing',
      heading: '5. Manual Clearing — จับคู่ปิดรายการ',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Manual Clearing รวมงานจับคู่ปิดทั้งระบบไว้ที่เดียว ด้านบนมีการ์ดอธิบาย 3 กรณีการจับคู่ (จับคู่เต็มจำนวน / จับคู่บางส่วน / ตัดจำหน่ายส่วนต่าง) ถัดมาเป็น worklist รายการค้าง คอลัมน์: บัญชี · Match Key · Ref · คำอธิบาย · ยอด · มีคู่? โดยรายการที่มีคู่หักล้างจะติดป้าย “มีคู่” ช่วยให้เห็นว่ารายการไหนพร้อมปิด ส่วนตารางกลุ่มที่จับคู่แล้ว (Match ID · รายการ · การกระทำ) มีปุ่ม ยกเลิกการจับคู่ เพื่อเปิดรายการกลับมาใหม่หากจับคู่ผิด',
        },
        {
          kind: 'image',
          src: '/help/glsched/04-manual-clearing.png',
          caption: 'แท็บ Manual Clearing — การ์ด 3 กรณี และ worklist Open Items พร้อมป้าย “มีคู่”',
        },
      ],
    },
    {
      id: 'autoclear',
      heading: '6. Auto-clear (ตรวจก่อนทำ)',
      blocks: [
        {
          kind: 'para',
          text: 'ที่แท็บ Manual Clearing กดปุ่ม Auto-clear (ตรวจก่อนทำ) ระบบจะ ตรวจแบบ dry-run (ยังไม่ลงจริง) แล้วเปิดโมดอล “ผลการตรวจสอบ Auto-clear” แสดงกลุ่มที่จับคู่ปิดได้ (ผลรวม = 0) เป็นตาราง บัญชี · Match Key · รายการ · ยอดรวม ตรวจความถูกต้องแล้วกด ยืนยันเคลียร์ N กลุ่ม เพื่อปิดทุกกลุ่มพร้อมกัน หากไม่พบกลุ่มที่หักล้างกันได้จะขึ้น “ไม่พบกลุ่มที่จับคู่ปิดได้”',
        },
        {
          kind: 'callout',
          text: 'Auto-clear จะทำงานได้ดีเมื่อบัญชีตั้งค่า Auto-clear เป็นเปิด และทุก JV มี Reference Key ที่ถูกต้อง (matchKey ตรงกันจึงจับคู่อัตโนมัติได้) — ตั้งค่าเหล่านี้ได้ที่แท็บตั้งค่าบัญชีที่ติดตาม',
          variant: 'info',
        },
        {
          kind: 'image',
          src: '/help/glsched/05-auto-clear-preview.png',
          caption: 'โมดอลผลตรวจ Auto-clear (dry-run) — กลุ่มที่ผลรวม = 0 พร้อมปุ่ม ยืนยันเคลียร์',
        },
      ],
    },
    {
      id: 'settings',
      heading: '7. ตั้งค่าบัญชีที่ติดตาม',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ตั้งค่าบัญชีที่ติดตาม เป็นตารางตั้งกฎต่อบัญชี ปรับได้ในแถว (แก้แล้วบันทึกทันที): Clearing Rule · Auto-clear (เปิด/ปิดจับคู่อัตโนมัติ) · บังคับใส่ Ref ตอนลง JV · Tolerance (฿) และ Tolerance (%) (วงเงินผลต่างที่ยอมให้ถือว่ากระทบยอดตรง/ตัดจำหน่ายได้) · บัญชีตัดจำหน่าย (บัญชีที่ใช้ลง JV write-off) · Active ใช้กำหนดว่าบัญชีใดเข้มงวดเรื่อง Reference Key และจับคู่อัตโนมัติเพียงใด',
        },
        {
          kind: 'image',
          src: '/help/glsched/06-settings-tracked-accounts.png',
          caption:
            'แท็บตั้งค่าบัญชีที่ติดตาม — Clearing Rule / Auto-clear / บังคับ Ref / Tolerance / บัญชีตัดจำหน่าย',
        },
      ],
    },
    {
      id: 'add-account',
      heading: '8. การเพิ่มบัญชีติดตามใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'ที่แท็บตั้งค่า กดปุ่ม เพิ่มบัญชีติดตาม จะเปิดโมดอลให้ เลือกบัญชีจาก COA (กล่องค้นหา) ใส่ คำอธิบาย และเลือก ประเภทการติดตาม',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'OPEN_ITEM — ',
              text: 'ติดตามเป็นรายรายการ (มี Open Items ให้จับคู่ปิด) เหมาะกับบัญชีลูกหนี้/เจ้าหนี้/เงินทดรอง',
            },
            {
              lead: 'RECON_ONLY — ',
              text: 'กระทบยอดอย่างเดียว (เทียบยอด GL กับ Schedule โดยไม่จับคู่รายการ)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'กรอกครบแล้วกด เพิ่มบัญชี (หรือ ยกเลิก เพื่อปิด) บัญชีใหม่จะปรากฏในตารางภาพรวมและตั้งค่าทันที',
        },
        {
          kind: 'image',
          src: '/help/glsched/07-add-account-modal.png',
          caption:
            'โมดอลเพิ่มบัญชีติดตาม — เลือกบัญชีจาก COA และประเภทการติดตาม (OPEN_ITEM / RECON_ONLY)',
        },
      ],
    },
    {
      id: 'close-period',
      heading: '9. การปิดงวด & Lock (4-eyes)',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อกระทบยอดเรียบร้อยทั้งงวด กดปุ่ม ปิดงวด & Lock ที่แถบบน โมดอลจะสรุปการกระทบยอด (บัญชีที่กระทบยอดแล้ว, ผลรวม |Delta| ทั้งหมด, สถานะ Snapshot + ผู้จัดทำ) แล้วให้ทำตาม 3 ขั้นตอน',
        },
        {
          kind: 'steps',
          items: [
            {
              title: 'จัดทำ Snapshot',
              text: 'กด จัดทำ Snapshot เพื่อตรึงยอดกระทบยอดของงวด (สถานะเป็น DRAFT — รอรับรอง)',
            },
            {
              title: 'รับรอง (Certify)',
              text: 'ผู้รับรองตรวจแล้วกดรับรอง — ต้องไม่ใช่คนเดียวกับผู้จัดทำ (4-eyes SoD) มิฉะนั้นระบบเตือน “ผู้รับรองต้องไม่ใช่ผู้จัดทำ” (สถานะเป็น CERTIFIED)',
            },
            {
              title: 'ล็อกงวด (Lock)',
              text: 'กดล็อกเพื่อปิดงวดถาวร — ล็อกแล้วแก้ไขไม่ได้อีก (สถานะเป็น LOCKED 🔒)',
            },
          ],
        },
        {
          kind: 'callout',
          text: 'หากยังมีบัญชีที่ยังไม่กระทบยอด ระบบจะ เตือน แต่ไม่บล็อกการปิดงวด — ผู้รับรองเป็นตัวควบคุมว่าจะปิดงวดหรือไม่ ควรแก้ผลต่างให้ครบก่อนปิด',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/glsched/08-close-period-lock.png',
          caption:
            'โมดอลปิดงวด & Lock — สรุปกระทบยอด และ 3 ขั้นตอน จัดทำ → รับรอง (4-eyes SoD) → ล็อก',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '10. สรุปสถานะที่พบ',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['งวดนี้: DRAFT', 'จัดทำ Snapshot แล้ว รอผู้รับรอง'],
            ['งวดนี้: รับรองแล้ว ✓ (CERTIFIED)', 'ผ่านการรับรองโดยผู้รับรอง รอการล็อก'],
            ['งวดนี้: ล็อกแล้ว 🔒 (LOCKED)', 'ปิดงวดถาวรแล้ว แก้ไขไม่ได้อีก'],
            ['ตรงกับ Schedule ✓', 'ยอด GL = ผลรวม Open Items (อยู่ในวงเงิน Tolerance)'],
            ['ผลต่าง {delta} ✗', 'ยอด GL ยังไม่ตรงกับ Schedule — ต้องตรวจ/จับคู่เพิ่ม'],
            ['ไม่ระบุ / กำลังติดตาม / ยกระดับ / แก้ไขแล้ว', 'สถานะการติดตาม Open Item แต่ละรายการ'],
            ['มีคู่', 'รายการนี้มีรายการอื่นที่หักล้างกันได้ พร้อมจับคู่ปิด'],
            ['Active / Inactive', 'บัญชีติดตามเปิดใช้งานอยู่ / ปิดใช้งาน'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '11. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ตารางบัญชีว่าง / ไม่มี Open Items?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'กดปุ่ม Sync รายการใหม่ เพื่อดึง JV ล่าสุดเข้า GL Schedule (เป็น idempotent กดซ้ำได้) หากยังไม่มีบัญชี ให้เพิ่มที่แท็บตั้งค่าบัญชีที่ติดตาม',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่ม จับคู่ปิด (Match) กดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ต้องเลือกตั้งแต่ 2 รายการขึ้นไปและผลรวมต้องเป็น 0 (ขึ้นป้าย “ผลรวม ✓”) ถ้าผลรวมยังไม่เป็น 0 ให้ใช้ จับคู่บางส่วน หรือ ตัดจำหน่ายส่วนต่าง แทน',
            },
          ],
        },
        { kind: 'para', text: 'รับรอง Snapshot ไม่ได้ ขึ้นว่าผู้รับรองต้องไม่ใช่ผู้จัดทำ?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เป็นการแบ่งแยกหน้าที่ (4-eyes SoD) ผู้รับรองต้องเป็นคนละคนกับผู้จัดทำ Snapshot ให้คนที่สองเข้ามารับรอง',
            },
          ],
        },
        { kind: 'para', text: 'ตัดจำหน่ายส่วนต่าง (Write-off) ใช้เมื่อไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ใช้เมื่อมีเศษส่วนต่างเล็กน้อยที่อยู่ในวงเงิน Tolerance ของบัญชี ระบบจะลง JV ตัดจำหน่ายเข้าบัญชีตัดจำหน่ายที่ตั้งไว้ ต้องระบุเหตุผล (Reason Code)',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '12. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Open Item — ',
              text: 'รายการค้างในบัญชีที่ยังไม่ถูกจับคู่ปิด (ยังเปิดอยู่)',
            },
            {
              lead: 'Reference Key / Match Key — ',
              text: 'รหัสอ้างอิงที่ใช้จับคู่รายการที่หักล้างกันให้ปิดได้ (อัตโนมัติเมื่อ matchKey ตรงกัน)',
            },
            {
              lead: 'Schedule (รายละเอียดประกอบบัญชี) — ',
              text: 'รายการประกอบยอดบัญชี ผลรวมต้องเท่ากับยอด GL คงเหลือ',
            },
            {
              lead: 'Delta (ผลต่าง) — ',
              text: 'ยอด GL คงเหลือ ลบ ผลรวม Open Items ถ้าอยู่ในวงเงิน Tolerance ถือว่ากระทบยอดตรง',
            },
            {
              lead: 'Tolerance — ',
              text: 'วงเงินผลต่าง (฿ หรือ %) ที่ยอมให้ถือว่ากระทบยอดตรงหรือตัดจำหน่ายได้',
            },
            {
              lead: 'Auto-clear — ',
              text: 'การปิดรายการอัตโนมัติเมื่อกลุ่มที่ matchKey ตรงกันมีผลรวม = 0',
            },
            {
              lead: 'Write-off (ตัดจำหน่าย) — ',
              text: 'การลง JV ตัดเศษส่วนต่างที่อยู่ในวงเงิน Tolerance ออก พร้อมเหตุผล',
            },
            {
              lead: 'Snapshot — ',
              text: 'การตรึงยอดกระทบยอดของงวดเพื่อปิดงวด (DRAFT → CERTIFIED → LOCKED)',
            },
            {
              lead: '4-eyes SoD — ',
              text: 'การแบ่งแยกหน้าที่ ผู้รับรองต้องไม่ใช่ผู้จัดทำ Snapshot',
            },
          ],
        },
      ],
    },
  ],
};
